import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { ServiceUser } from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCreateServiceUser,
  useUpdateServiceUser,
} from "@/features/service-users/hooks/useServiceUsers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceUser?: ServiceUser | null;
}

export function ServiceUserFormDialog({
  open,
  onOpenChange,
  serviceUser,
}: Props) {
  const { t } = useTranslation("serviceUsers");
  const { t: tc } = useTranslation("common");
  const create = useCreateServiceUser();
  const update = useUpdateServiceUser(serviceUser?.id ?? "");
  const isEdit = Boolean(serviceUser);
  const [name, setName] = useState("");

  useEffect(() => {
    if (open) setName(serviceUser?.name ?? "");
  }, [open, serviceUser]);

  const pending = create.isPending || update.isPending;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      if (isEdit && serviceUser) {
        await update.mutateAsync({ name: trimmed });
        toast.success(t("toasts.updated"));
      } else {
        await create.mutateAsync({ name: trimmed });
        toast.success(t("toasts.created"));
      }
      onOpenChange(false);
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("form.titleEdit") : t("form.titleNew")}
          </DialogTitle>
          <DialogDescription>{t("form.description")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="su-name">{t("form.name")}</Label>
            <Input
              id="su-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("form.namePlaceholder")}
              maxLength={255}
              autoFocus
              required
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {tc("actions.cancel")}
            </Button>
            <Button type="submit" disabled={pending || !name.trim()}>
              {isEdit ? t("form.save") : t("form.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
